import React from 'react';
import { Slider } from '../ui/slider';
import { fmtNum } from '../../utils/formatArcaNumbers';
import AdvisorTip from './AdvisorTip';

export interface ReportSettings {
	/** Años hasta la jubilación (horizonte). */
	years: number;
	/** Saldo declarado en el plan actual (€). */
	balance: number;
	/** Aportación mensual actual (€); 0 si no aporta. */
	monthlyContribution: number;
	/** Porcentaje del saldo a traspasar (10-100). */
	transferPct: number;
}

interface ReportSettingsCardProps {
	settings: ReportSettings;
	onChange: (patch: Partial<ReportSettings>) => void;
	/** Aportación mensual máxima deducible (€): 125 individual, ~479 autónomo. */
	maxMonthly: number;
	/**
	 * 'transfer' (informe de traspaso): años, saldo, aportación y % a traspasar.
	 * 'newPlan' (plan desde cero, sin origen): solo años y aportación — no hay saldo
	 * que declarar ni porcentaje que mover.
	 */
	variant?: 'transfer' | 'newPlan';
	translate: (key: string) => string;
}

/**
 * Card de ajustes del informe (columna izquierda). Deja al usuario retocar los datos
 * del onboarding —años a jubilación, saldo, aportación actual y % a traspasar— para
 * afinar la comparación de la derecha. Componente controlado.
 */
const ReportSettingsCard: React.FC<ReportSettingsCardProps> = ({
	settings,
	onChange,
	maxMonthly,
	variant = 'transfer',
	translate
}) => {
	const { years, balance, monthlyContribution, transferPct } = settings;
	const transferredAmount = (balance * transferPct) / 100;
	const isNewPlan = variant === 'newPlan';

	// Input de saldo con separador de miles: mostramos formateado y parseamos solo dígitos.
	const handleBalanceChange = (raw: string) => {
		const digits = raw.replace(/\D/g, '');
		onChange({ balance: digits === '' ? 0 : parseInt(digits, 10) });
	};

	return (
		<div className="surface-card p-5 space-y-5 h-full">
			<div className="space-y-0.5">
				<p className="text-base font-semibold">
					{isNewPlan
						? translate('individual.reportSettings.titleNewPlan') || 'Ajusta tu plan'
						: translate('individual.reportSettings.title') || 'Ajusta tu informe'}
				</p>
				<p className="text-xs text-muted-foreground leading-relaxed">
					{isNewPlan
						? translate('individual.reportSettings.subtitleNewPlan') ||
							'Elige cuánto quieres aportar cada mes para ver cómo crece.'
						: translate('individual.reportSettings.subtitle') ||
							'Retoca los datos que nos diste para afinar la comparación.'}
				</p>
			</div>

			{/* Años hasta jubilación */}
			<div className="space-y-2">
				<div className="flex justify-between text-sm">
					<span className="text-muted-foreground">
						{translate('individual.reportSettings.years') || 'Años hasta tu jubilación'}
					</span>
					<span className="font-semibold">
						{years} {translate('individual.reportSettings.yearsUnit') || 'años'}
					</span>
				</div>
				<div className="slider-arca">
					<Slider
						min={1}
						max={47}
						step={1}
						value={[years]}
						onValueChange={([v]) => onChange({ years: v })}
					/>
				</div>
			</div>

			{/* Dinero en tu plan — solo traspaso (en plan nuevo se empieza desde 0). */}
			{!isNewPlan && (
				<div className="space-y-1.5">
					<label className="text-sm text-muted-foreground" htmlFor="report-balance">
						{translate('individual.reportSettings.balance') || 'Dinero en tu plan actual'}
					</label>
					<div className="flex items-center gap-2">
						<input
							id="report-balance"
							type="text"
							inputMode="numeric"
							value={balance === 0 ? '' : fmtNum(balance, 0)}
							placeholder={
								translate('individual.reportSettings.balancePlaceholder') || '¿Cuánto tienes?'
							}
							onChange={(e) => handleBalanceChange(e.target.value)}
							className="flex-1 min-w-0 rounded-xl border border-border bg-background px-3 py-2.5 text-base font-semibold focus:outline-hidden focus:ring-2 focus:ring-primary/40"
						/>
						<span className="text-base font-semibold text-muted-foreground shrink-0">€</span>
					</div>
				</div>
			)}

			{/* Aportación mensual (en traspaso: la actual, preseteada a 0 si no aporta;
			    en plan nuevo: la que quiere empezar a aportar). */}
			<div className="space-y-2">
				<div className="flex justify-between text-sm">
					<span className="text-muted-foreground">
						{isNewPlan
							? translate('individual.reportSettings.contributeQuestionNewPlan') ||
								'¿Cuánto quieres aportar al mes?'
							: translate('individual.reportSettings.contributingQuestion') ||
								'Tu aportación mensual'}
					</span>
					<span className="font-semibold">{monthlyContribution} €/mes</span>
				</div>
				<div className="slider-arca">
					<Slider
						min={isNewPlan ? 10 : 0}
						max={maxMonthly}
						step={5}
						value={[Math.min(monthlyContribution, maxMonthly)]}
						onValueChange={([v]) => onChange({ monthlyContribution: v })}
					/>
				</div>
			</div>

			<AdvisorTip>
				{isNewPlan ? (
					<>
						<strong className="font-semibold">
							{translate('individual.reportSettings.contributeTipNewPlan.highlight') ||
								'Te recomendamos al menos 30 €/mes.'}
						</strong>{' '}
						{translate('individual.reportSettings.contributeTipNewPlan.body') ||
							'Puedes empezar desde 10 € e ir poco a poco, pero a partir de 30 € al mes el ahorro y la rentabilidad se vuelven significativos a largo plazo.'}
					</>
				) : (
					<>
						<strong className="font-semibold">
							{translate('individual.reportSettings.contributeTip.highlight') ||
								'Aunque sea poco, merece la pena.'}
						</strong>{' '}
						{translate('individual.reportSettings.contributeTip.body') ||
							'Puedes dejarlo a 0 €, pero con solo 30 €/mes lograrás un crecimiento sorprendente y ventajas en tu IRPF.'}
					</>
				)}
			</AdvisorTip>

			{/* % a traspasar + su tip — solo traspaso (en plan nuevo no hay saldo que mover). */}
			{!isNewPlan && (
				<>
					<div className="space-y-2">
						<div className="flex justify-between text-sm">
							<span className="text-muted-foreground">
								{translate('individual.reportSettings.transferPct') ||
									'¿Cuánto quieres mover al nuevo plan?'}
							</span>
							<span className="font-semibold">{transferPct}%</span>
						</div>
						<div className="slider-arca">
							<Slider
								min={25}
								max={100}
								step={25}
								value={[transferPct]}
								onValueChange={([v]) => onChange({ transferPct: v })}
							/>
						</div>
						<p className="text-right text-sm font-semibold text-primary">
							= {fmtNum(transferredAmount, 0)} €
						</p>
					</div>

					<AdvisorTip>
						<strong className="font-semibold">
							{translate('individual.reportSettings.tip.highlight') ||
								'No tienes que traspasarlo todo.'}
						</strong>{' '}
						{translate('individual.reportSettings.tip.body') ||
							'Dejar una parte en tu plan actual reparte el riesgo entre varias gestoras, una práctica sana para no depender de un solo plan. Y mover el resto más adelante es siempre gratis.'}
					</AdvisorTip>
				</>
			)}
		</div>
	);
};

export default ReportSettingsCard;
